import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { sendPaymentSuccessEmail, generateOrderId } from '@/lib/sendPaymentEmail';

interface Props {
  videoTitle: string;
  price: number;
  onClose: () => void;
  onSuccess: () => void;
}

type PayMethod = 'wechat' | 'alipay' | 'credit' | 'debit';

const payMethods: { key: PayMethod; label: string; icon: string; color: string; activeBg: string; activeBorder: string; activeText: string }[] = [
  { key: 'wechat', label: '微信支付', icon: 'ri-wechat-pay-fill', color: 'text-green-600', activeBg: 'bg-green-50', activeBorder: 'border-green-500', activeText: 'text-green-700' },
  { key: 'alipay', label: '支付宝', icon: 'ri-alipay-fill', color: 'text-sky-500', activeBg: 'bg-sky-50', activeBorder: 'border-sky-400', activeText: 'text-sky-700' },
  { key: 'credit', label: '信用卡', icon: 'ri-bank-card-fill', color: 'text-orange-500', activeBg: 'bg-orange-50', activeBorder: 'border-orange-400', activeText: 'text-orange-700' },
  { key: 'debit', label: '借记卡', icon: 'ri-secure-payment-fill', color: 'text-gray-600', activeBg: 'bg-gray-50', activeBorder: 'border-gray-400', activeText: 'text-gray-700' },
];

// Deterministic QR pattern based on seed
function QRPattern({ seed, size = 120 }: { seed: number; size?: number }) {
  const cells = 21;
  const pattern = Array.from({ length: cells * cells }, (_, i) => {
    const row = Math.floor(i / cells);
    const col = i % cells;
    // Finder patterns (corners)
    const inFinder = (
      (row < 7 && col < 7) ||
      (row < 7 && col >= cells - 7) ||
      (row >= cells - 7 && col < 7)
    );
    if (inFinder) {
      const r = row % 7;
      const c = col % (col < 7 ? 7 : cells - col < 7 ? 7 : 7);
      const localR = row < 7 ? row : row - (cells - 7);
      const localC = col < 7 ? col : col - (cells - 7);
      return (localR === 0 || localR === 6 || localC === 0 || localC === 6 || (localR >= 2 && localR <= 4 && localC >= 2 && localC <= 4));
    }
    // Data modules
    return ((i * 7 + seed * 13 + row * 3 + col * 5) % 3 !== 0);
  });
  const cellSize = size / cells;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} xmlns="http://www.w3.org/2000/svg">
      {pattern.map((filled, i) => {
        if (!filled) return null;
        const row = Math.floor(i / cells);
        const col = i % cells;
        return (
          <rect
            key={i}
            x={col * cellSize}
            y={row * cellSize}
            width={cellSize}
            height={cellSize}
            fill="#1a1a1a"
          />
        );
      })}
    </svg>
  );
}

export default function PaymentModal({ videoTitle, price, onClose, onSuccess }: Props) {
  const [method, setMethod] = useState<PayMethod>('wechat');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);
  const [agree, setAgree] = useState(true);
  const [countdown, setCountdown] = useState(300); // 5 min QR expiry
  const [email, setEmail] = useState('');
  const [orderId, setOrderId] = useState('');

  useEffect(() => {
    if ((method === 'wechat' || method === 'alipay') && !paid) {
      setCountdown(300);
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) { clearInterval(timer); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [method, paid]);

  const formatCardNumber = (v: string) => {
    const digits = v.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (v: string) => {
    const digits = v.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return digits;
  };

  const formatCountdown = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handlePay = async (e: React.FormEvent) => {
    e.preventDefault();
    setPaying(true);
    await new Promise(r => setTimeout(r, 2000));
    const newOrderId = generateOrderId('CRS');
    setOrderId(newOrderId);
    // Send email notification if email provided
    if (email.trim()) {
      await sendPaymentSuccessEmail({
        email: email.trim(),
        orderId: newOrderId,
        itemName: videoTitle,
        amount: price,
        payMethod: method,
        type: 'course',
      });
    }
    setPaying(false);
    setPaid(true);
    setTimeout(() => { onSuccess(); }, 3000);
  };

  const isCard = method === 'credit' || method === 'debit';
  const isQR = method === 'wechat' || method === 'alipay';

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md rounded-3xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="font-black text-gray-900 text-lg">安全支付</h2>
            <p className="text-xs text-gray-400 mt-0.5">金榜旺财 · SSL加密保护</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 cursor-pointer transition-colors">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {paid ? (
          <div className="p-8 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-5xl text-green-500"></i>
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">支付成功！</h3>
            <p className="text-gray-500 text-sm mb-1">课程已解锁，立即开始学习吧</p>
            {orderId && (
              <p className="text-xs text-gray-400 font-mono mb-4">订单号：{orderId}</p>
            )}
            {email && (
              <div className="bg-green-50 border border-green-100 rounded-xl px-4 py-3 mb-5 flex items-center gap-2">
                <i className="ri-mail-check-line text-green-600 text-base flex-shrink-0"></i>
                <p className="text-xs text-green-700 text-left">支付凭证已发送至 <strong>{email}</strong>，请注意查收</p>
              </div>
            )}
            <div className="flex gap-3">
              <Link
                to="/orders"
                onClick={onClose}
                className="flex-1 py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-full cursor-pointer hover:border-red-300 hover:text-red-600 transition-colors whitespace-nowrap text-sm"
              >
                <i className="ri-list-check mr-1"></i>查看订单
              </Link>
              <button
                onClick={onSuccess}
                className="flex-1 py-3 bg-red-600 text-white font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap text-sm"
              >
                开始学习
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handlePay} className="p-6 flex flex-col gap-4">
            {/* Order Summary */}
            <div className="bg-gray-50 rounded-2xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-xl flex-shrink-0">
                <i className="ri-video-line text-red-600"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400 mb-0.5">购买课程</p>
                <p className="font-bold text-gray-900 text-sm line-clamp-1">{videoTitle}</p>
              </div>
              <span className="text-2xl font-black text-red-600 whitespace-nowrap">¥{price}</span>
            </div>

            {/* Email for receipt */}
            <div>
              <label className="text-xs font-bold text-gray-500 mb-1.5 block uppercase tracking-wide">接收支付凭证（选填）</label>
              <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                <i className="ri-mail-line text-gray-400 text-sm"></i>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="填写邮箱，支付成功后发送凭证"
                  className="flex-1 bg-transparent text-sm outline-none"
                />
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <label className="text-xs font-bold text-gray-500 mb-2 block uppercase tracking-wide">选择支付方式</label>
              <div className="grid grid-cols-2 gap-2">
                {payMethods.map(m => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMethod(m.key)}
                    className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                      method === m.key
                        ? `${m.activeBg} ${m.activeBorder}`
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <i className={`${m.icon} text-xl ${method === m.key ? m.color : 'text-gray-300'}`}></i>
                    <span className={`text-sm font-semibold ${method === m.key ? m.activeText : 'text-gray-400'}`}>{m.label}</span>
                    {method === m.key && <i className="ri-check-line text-xs ml-auto text-current opacity-70"></i>}
                  </button>
                ))}
              </div>
            </div>

            {/* WeChat Pay QR */}
            {method === 'wechat' && (
              <div className="bg-gradient-to-b from-green-50 to-white border border-green-100 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-wechat-pay-fill text-green-600 text-xl"></i>
                  <span className="font-bold text-green-800 text-sm">微信扫码支付</span>
                  <span className="ml-auto text-xs text-gray-400 flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {countdown > 0 ? `${formatCountdown(countdown)} 后过期` : '二维码已过期'}
                  </span>
                </div>
                <div className="flex justify-center mb-4">
                  <div className={`relative p-3 bg-white border-2 rounded-2xl transition-all ${countdown === 0 ? 'border-gray-200 opacity-40' : 'border-green-200'}`}>
                    <QRPattern seed={price + 42} size={140} />
                    {countdown === 0 && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/90 rounded-xl">
                        <div className="text-center">
                          <i className="ri-refresh-line text-gray-400 text-2xl block mb-1"></i>
                          <span className="text-xs text-gray-500">点击刷新</span>
                        </div>
                      </div>
                    )}
                    {/* Center logo */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-8 h-8 flex items-center justify-center bg-green-500 rounded-lg">
                        <i className="ri-wechat-pay-fill text-white text-base"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-700 mb-1">打开微信，扫描上方二维码</p>
                  <p className="text-xs text-gray-400">支付金额：<strong className="text-red-600 text-sm">¥{price}</strong></p>
                </div>
                <div className="mt-4 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5 flex items-start gap-2">
                  <i className="ri-information-line text-amber-500 text-sm flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    当前为演示模式。正式上线后将显示真实微信支付二维码，扫码即可完成支付。
                  </p>
                </div>
              </div>
            )}

            {/* Alipay QR */}
            {method === 'alipay' && (
              <div className="bg-gradient-to-b from-sky-50 to-white border border-sky-100 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-alipay-fill text-sky-500 text-xl"></i>
                  <span className="font-bold text-sky-800 text-sm">支付宝扫码支付</span>
                  <span className="ml-auto text-xs text-gray-400 flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {countdown > 0 ? `${formatCountdown(countdown)} 后过期` : '二维码已过期'}
                  </span>
                </div>
                <div className="flex justify-center mb-4">
                  <div className={`relative p-3 bg-white border-2 rounded-2xl transition-all ${countdown === 0 ? 'border-gray-200 opacity-40' : 'border-sky-200'}`}>
                    <QRPattern seed={price + 88} size={140} />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-8 h-8 flex items-center justify-center bg-sky-500 rounded-lg">
                        <i className="ri-alipay-fill text-white text-base"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-700 mb-1">打开支付宝，扫描上方二维码</p>
                  <p className="text-xs text-gray-400">支付金额：<strong className="text-red-600 text-sm">¥{price}</strong></p>
                </div>
                <div className="mt-4 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5 flex items-start gap-2">
                  <i className="ri-information-line text-amber-500 text-sm flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    当前为演示模式。正式上线后将显示真实支付宝二维码，扫码即可完成支付。
                  </p>
                </div>
              </div>
            )}

            {/* Card Fields */}
            {isCard && (
              <div className="flex flex-col gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1.5 block">
                    {method === 'credit' ? '信用卡' : '借记卡'}卡号
                  </label>
                  <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-orange-400 transition-colors">
                    <i className="ri-bank-card-line text-gray-400"></i>
                    <input
                      type="text"
                      value={cardNumber}
                      onChange={e => setCardNumber(formatCardNumber(e.target.value))}
                      placeholder="1234 5678 9012 3456"
                      maxLength={19}
                      className="flex-1 bg-transparent text-sm outline-none font-mono tracking-wider"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1.5 block">持卡人姓名</label>
                  <input
                    type="text"
                    value={cardHolder}
                    onChange={e => setCardHolder(e.target.value)}
                    placeholder="请输入持卡人姓名"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-orange-400 transition-colors"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-gray-500 mb-1.5 block">有效期</label>
                    <input
                      type="text"
                      value={cardExpiry}
                      onChange={e => setCardExpiry(formatExpiry(e.target.value))}
                      placeholder="MM/YY"
                      maxLength={5}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-orange-400 transition-colors font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 mb-1.5 block">CVV / CVC</label>
                    <input
                      type="password"
                      value={cardCvv}
                      onChange={e => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="3-4位"
                      maxLength={4}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-orange-400 transition-colors font-mono"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Agreement */}
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={agree}
                onChange={e => setAgree(e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-red-600 cursor-pointer flex-shrink-0"
              />
              <span className="text-xs text-gray-500 leading-relaxed">
                我已阅读并同意
                <span className="text-red-600 mx-1 hover:underline cursor-pointer">购买协议</span>
                和
                <span className="text-red-600 mx-1 hover:underline cursor-pointer">退款政策</span>
              </span>
            </label>

            {/* Pay Button */}
            {isCard && (
              <button
                type="submit"
                disabled={paying || !agree}
                className="w-full py-4 bg-red-600 text-white rounded-full font-black text-base hover:bg-red-700 transition-all cursor-pointer disabled:opacity-60"
              >
                {paying ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>支付中...
                  </span>
                ) : `立即支付 ¥${price}`}
              </button>
            )}

            {isQR && (
              <button
                type="submit"
                disabled={paying || !agree || countdown === 0}
                className={`w-full py-4 text-white rounded-full font-black text-base transition-all cursor-pointer disabled:opacity-60 ${
                  method === 'wechat' ? 'bg-green-600 hover:bg-green-500' : 'bg-sky-500 hover:bg-sky-400'
                }`}
              >
                {paying ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>确认中...
                  </span>
                ) : countdown === 0 ? '二维码已过期，请刷新' : '我已完成扫码支付'}
              </button>
            )}

            <div className="flex items-center justify-center gap-5 text-xs text-gray-400">
              <span className="flex items-center gap-1"><i className="ri-shield-check-line text-green-500"></i>安全加密</span>
              <span className="flex items-center gap-1"><i className="ri-lock-line text-green-500"></i>SSL保护</span>
              <span className="flex items-center gap-1"><i className="ri-refund-2-line text-green-500"></i>7天退款</span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
